import React, { useMemo, useState } from 'react';

import ProcurementEditForm from './ProcurementEditForm';

import { idempotencyHeader } from 'Helpers/apiUtils';
import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { isSetupGateError } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const mutationHeader = idempotencyHeader();

export default function ProcurementPrModal(props) {
    const [showDetails, setShowDetails] = useState(false);
    const [requestFailed, setRequestFailed] = useState(false);
    const [request, setRequest] = useState(false);

    const titles = useMemo(() => {
        const procurement = props.procurement;

        if (procurement) {
            const procurementPolicy = procurement.procurement_policy;
            const titles = [
                $t('You are requesting approval to begin procuring {{name}} with an estimated value of {{value}}.', {
                    name: procurement.name,
                    value: formatToFixedMoneyWithLocale(procurement.value),
                }),
            ];

            if (procurementPolicy) {
                titles.push($t('The procurement policy states: {{policy_details}}', { policy_details: procurementPolicy.details }));
            }

            return titles;
        }
    }, [props.procurement]);

    const onPrConfirm = () => {
        setRequest(true);
        props.setLoading('data', true);
        const url = storePRUrl.replace('ITEM_ID', props.procurement.id);

        axios
            .post(
                url,
                {
                    procurement_policy_id: props.procurement.policy_id,
                    title: props.procurement.name,
                    estimated_value: props.procurement.value || 0,
                },
                {
                    headers: { ...mutationHeader },
                },
            )
            .then(() => {
                window.location.href = props.indexPRUrl;
            })
            .catch((error) => {
                // The set-up gate's own message is already shown by the API client.
                if (!isSetupGateError(error)) {
                    setRequestFailed(true);
                }
            })
            .finally(() => {
                setRequest(false);
                props.setLoading('data', false);
            });
    };

    return (
        <Modal id={props.id} modal={props.modal}>
            {!!titles?.length && (
                <ModalHeader dark close={props.onClose}>
                    <div>
                        {titles[0]}
                        <br />
                        {titles[1]}
                    </div>
                </ModalHeader>
            )}
            <div className="tw-rounded-b">
                {props.loading ? (
                    <LoadingSpinner />
                ) : !props.fetched ? (
                    <div className="modal__form-container">
                        <label className="modal__msg modal__error-text-color">{$t('Data not fetched')}</label>
                    </div>
                ) : (
                    <div className="modal__form-container">
                        <div className="modal__footer">
                            <Button variant="danger" onClick={props.onCancel}>
                                {$t('Cancel')}
                            </Button>
                            {props.isCreatePrMode && (
                                <Button variant="primary" onClick={onPrConfirm} disabled={request}>
                                    {$t('Confirm')}
                                </Button>
                            )}
                        </div>
                        <ProcurementEditForm
                            procurement={props.procurement}
                            policies={props.policies}
                            categories={props.categories}
                            priorities={props.priorities}
                            complexities={props.complexities}
                            statuses={props.statuses}
                            expensesTree={props.expensesTree}
                            expensesIds={props.expensesIds}
                            hideDetails={!showDetails}
                            arrow={props.arrow}
                            saveUrl={props.saveUrl}
                            storePRUrl={props.storePRUrl}
                            indexPRUrl={props.indexPRUrl}
                            syncExpensesUrl={props.syncExpensesUrl}
                            downloadAttachmentUrl={props.downloadAttachmentUrl}
                            deleteAttachmentUrl={props.deleteAttachmentUrl}
                            existedIds={props.existedIds}
                            setOrdering={props.setOrdering}
                            isCreatePrMode={props.isCreatePrMode}
                            setLoading={props.setLoading}
                            closeHandler={props.onClose}
                            eventStartDate={props.eventStartDate}
                            eventEndDate={props.eventEndDate}
                            onEdit={props.onEdit}
                            onCancelEdit={props.onCancelEdit}
                            onCancel={props.onCancel}
                            onCreated={props.onCreated}
                            onExpensesSynced={props.onExpensesSynced}
                            onAttachmentDeleted={props.onAttachmentDeleted}
                            onSuccess={() => {
                                props.onClose();
                                props.onSuccess();
                            }}
                        />
                        <div className="modal__footer tw-mt-6">
                            <div className="tw-flex tw-items-center tw-cursor-pointer" onClick={() => setShowDetails(!showDetails)}>
                                <div className="tw-text-blue tw-text-2xl tw-font-semibold">
                                    {showDetails ? $t('Click here to hide details') : $t('Click here to see details')}
                                </div>
                                <div className="table__icon-btn tw-text-white tw-ml-4">
                                    {showDetails ? <MinusIcon className="tw-w-3.5 tw-h-3.5" /> : <PlusIcon className="tw-w-3.5 tw-h-3.5" />}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
